/**
 * Shopify's storefront Liquid API exposes only ONE progressive MP4 rendition
 * per video (the highest-resolution transcode) plus an HLS manifest - the
 * lower-bitrate progressive files visible in the admin are not reachable
 * from theme code. The HLS manifest is genuinely multi-bitrate, so this is
 * the only way to get real adaptive-bitrate delivery for a background/loop
 * video without re-uploading a smaller source file.
 *
 * window.initAdaptiveVideo(video) swaps a <video data-hls-src="…"> from its
 * static <source> (mp4) fallback onto that HLS stream, using native support
 * where available (Safari/iOS) and falling back to hls.js everywhere else.
 * Safe to call more than once per element - it only acts the first time.
 */
window.initAdaptiveVideo = function (video) {
  if (!video || video.dataset.adaptiveInit) return;
  const hlsSrc = video.dataset.hlsSrc;
  if (!hlsSrc) return;
  video.dataset.adaptiveInit = '1';

  let recovered = false;
  const useProgressiveFallback = () => {
    if (recovered) return;
    recovered = true;
    video.dataset.adaptiveFallbackUsed = '1';

    const fallbackSrc = video.dataset.videoFallbackSrc;
    if (!fallbackSrc) return;

    video.pause();
    if (video._cranbiHls) video._cranbiHls.destroy();
    video.removeAttribute('src');

    const source = video.querySelector('source');
    if (source) {
      source.src = fallbackSrc;
    } else {
      video.src = fallbackSrc;
    }
    video.load();
    const playback = video.play();
    if (playback && typeof playback.catch === 'function') playback.catch(() => {});
  };

  // Keep the poster/neutral frame if native HLS fails instead of leaving a
  // colored background in the hero. The progressive MP4 is already present
  // as a source and is the reliable recovery path on iOS.
  video.addEventListener('error', useProgressiveFallback, { once: true });

  if (video.canPlayType('application/vnd.apple.mpegurl')) {
    video.src = hlsSrc;
    video.load();
    return;
  }

  if (window.Hls && window.Hls.isSupported()) {
    const hls = new window.Hls({
      enableWorker: true,
      capLevelToPlayerSize: true,
      maxBufferLength: 8,
      maxMaxBufferLength: 16,
      backBufferLength: 0,
    });
    video._cranbiHls = hls;
    hls.on(window.Hls.Events.ERROR, (_event, data) => {
      if (data?.fatal) useProgressiveFallback();
    });
    hls.loadSource(hlsSrc);
    hls.attachMedia(video);
    return;
  }

  // Neither native HLS nor hls.js is available (very old browser). The MP4
  // <source> already in the DOM plays as-is, just without adaptive bitrate.
};
